<template>
  <component
    :is="tagName"
    class="Button"
    :class="[`Button--${dynamics}`]"
    :href="href"
    :disabled="isDisabled"
  >
    <Icon v-if="iconName" class="Button__icon" :icon-name="iconName" />
    {{ label }}
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Icon from '@/components/atoms/IconBase.vue';

defineOptions({
  name: 'ButtonBase',
});

interface Props {
  tag?: string;
  label: string;
  dynamics?: 'primary' | 'secondary' | 'tertiary' | 'tertiaryBlack' | 'danger';
  iconName?: string;
  href?: string;
  isDisabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  tag: '',
  dynamics: 'primary',
  iconName: '',
  href: '',
  isDisabled: false,
});

const tagName = computed(() => {
  return props.tag;
});
</script>

<style lang="scss" scoped>
@use '@/assets/scss/_forward.scss' as *;

.Button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  cursor: pointer;
  border-radius: $service-border-radius;
  box-shadow: $service-shadow-mp;
  min-width: $service-unit * 26;

  &--primary,
  &--secondary,
  &--danger {
    padding: $service-unit * 2 $service-unit * 5;
    font-size: $service-font-size-p;
  }

  &--primary {
    color: $color-white-100;
    background-color: $color-blue-60;
  }

  &--secondary,
  &--tertiary {
    color: $color-gray-80;
    background-color: $color-gray-20;
  }

  &--danger {
    color: $color-white-100;
    background-color: $color-red-80;
  }

  &--tertiary,
  &--tertiaryBlack {
    padding: $service-unit * 1.75 $service-unit * 4;
    font-size: $service-font-size-pp;
  }

  &--tertiaryBlack {
    color: $color-white-100;
    background-color: $color-gray-70;
  }

  &--primary:hover {
    background-color: $color-blue-80;
  }

  &--secondary:hover,
  &--tertiary:hover {
    background-color: $color-gray-40;
  }

  &--tertiaryBlack:hover {
    background-color: $color-gray-80;
  }

  &--danger:hover {
    background-color: #a11218;
  }

  &:hover {
    text-decoration: none;
  }

  &:disabled {
    color: $color-gray-50;
    cursor: inherit;
    background-color: $color-gray-30;
  }

  &:disabled &__icon {
    color: $color-gray-50;
  }

  &__icon {
    margin-right: $service-unit;
  }

  &--primary &__icon,
  &--tertiaryBlack &__icon {
    color: $color-white-100;
  }

  &--secondary &__icon,
  &--tertiary &__icon {
    color: $color-gray-80;
  }

  &--primary &__icon,
  &--secondary &__icon,
  &--danger &__icon {
    width: $service-unit * 6;
    height: $service-unit * 6;
  }

  &--tertiary &__icon,
  &--tertiaryBlack &__icon {
    width: $service-unit * 4;
    height: $service-unit * 4;
  }

  @media (max-width: 768px) {
    &--primary {
      padding: $service-unit * 2 0px;
      font-size: $service-font-size-pp;
      margin-bottom: $service-unit * 2;
    }
    &--secondary,
    &--danger {
      font-size: $service-font-size-mp;
      margin-bottom: $service-unit * 2;
    }
  }
}
</style>
